import { Nullable } from "sonobello.utilities.react";
import { ITelemetryEvent, TelemetrySeverity } from "sonobello.utilities.react.telemetry";

import { TelemetrySessionTags } from "../../../Telemetry/types/ITelemetryClient";

/** The summarized state of the Scheduling Loader. */
export enum SchedulingLoaderState {
  /** The initial state of the loader. */
  Started = "Started",
  /** The state after the first heartbeat check has occurred and the loader is still preventing use of the scheduling step. */
  Pending = "Pending",
  /** The state after the loader after enough time has passed the configured threshold where it should have reasonably completed. */
  Delinquent = "Delinquent",
  /** The state when the scheduling step has been rendered at least once. */
  Completed = "Completed"
}

const description =
  "DO NOT CONFLATE WITH TOTAL CALENDAR LOAD TIME. This is ONLY the time the user spent observing the scheduling step spinner.";

/** A telemetry event which indicates the scheduling loader has been waiting for the provided duration. */
export class SchedulingLoaderPendingEvent implements ITelemetryEvent<TelemetrySessionTags> {
  message = "Scheduling loader pending.";
  severity = TelemetrySeverity.Info;
  tags = { [TelemetrySessionTags.SchedulingStepLoading]: SchedulingLoaderState.Pending };
  miscellaneous: Nullable<Record<string, unknown>>;
  data: { timeElapsedMs: number; calendarsReady: number };

  constructor(
    timeElapsedMs: number,
    bookableCentersCount: number,
    bookingController: Nullable<Record<string, unknown>>
  ) {
    this.data = { timeElapsedMs, calendarsReady: bookableCentersCount };
    this.miscellaneous = { description, ...bookingController };
  }
}

/** A telemetry event which indicates the scheduling loader has been waiting for the provided duration. */
export class SchedulingLoaderDelinquentEvent implements ITelemetryEvent<TelemetrySessionTags> {
  message = "Scheduling loader delinquent.";
  severity = TelemetrySeverity.Error;
  tags = { [TelemetrySessionTags.SchedulingStepLoading]: SchedulingLoaderState.Delinquent };
  miscellaneous: Nullable<Record<string, unknown>>;
  data: { timeElapsedMs: number; calendarsReady: number };

  constructor(
    timeElapsedMs: number,
    bookableCentersCount: number,
    bookingController: Nullable<Record<string, unknown>>
  ) {
    this.data = { timeElapsedMs, calendarsReady: bookableCentersCount };
    this.miscellaneous = { description, ...bookingController };
  }
}

/** A telemetry event which indicates the scheduling loader has concluded waiting for the provided duration. */
export class SchedulingLoaderCompletedEvent implements ITelemetryEvent<TelemetrySessionTags> {
  message = "Scheduling loader completed.";
  severity = TelemetrySeverity.Info;
  tags = { [TelemetrySessionTags.SchedulingStepLoading]: SchedulingLoaderState.Completed };
  miscellaneous = { description };
  data: { timeElapsedMs: number };

  constructor(timeElapsedMs: number) {
    this.data = { timeElapsedMs };
  }
}
